import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Search, AlertTriangle, Info, XCircle, GraduationCap, Link2, X, Upload, CheckCircle2, KeyRound, ChevronRight, ArrowLeft } from 'lucide-react';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { BulkImport } from './BulkImport';
import { SuccessStep } from './add-student/SuccessStep';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { error as logError } from '../../../utils/logger';
import { validateLink, hasGatingWarning, hasBlockingWarning, getLearnerLinkageFlags, findAccountByLinkCode, isLearnerAccount, getLinkedLearners, type LinkWarning, type Classroom, type TeacherAccount, type LearnerAccount } from '../../../accounts';
// ── Constants ────────────────────────────────────────────────
const CODE_PATTERN = /^[A-Z0-9]{6}$/;
const SEARCH_MIN_LENGTH = 2;
const MAX_SEARCH_RESULTS = 8;
// ── Props ────────────────────────────────────────────────────
interface AddLearnerSheetProps {
  open: boolean;
  onClose: () => void;
  activeAccount: TeacherAccount;
  classrooms: Classroom[];
  selectedClassroomId?: string | null;
  onComplete?: () => void;
}
type SheetView = 'input' // smart input: search/code
| 'selected' // a learner is selected, show confirm with warnings
| 'new' // inline new-student form
| 'bulk' // bulk import sub-view
| 'success'; // success screen
// ── Warning chip styling ─────────────────────────────────────
const WARNING_STYLES: Record<LinkWarning['severity'], {
  bg: string;
  border: string;
  text: string;
  icon: string;
}> = {
  info: {
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    text: 'text-sky-900',
    icon: 'text-sky-500'
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-900',
    icon: 'text-amber-500'
  },
  blocking: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-900',
    icon: 'text-rose-500'
  }
};
function WarningRow({
  warning
}: {
  warning: LinkWarning;
}) {
  const style = WARNING_STYLES[warning.severity];
  const Icon = warning.severity === 'info' ? Info : warning.severity === 'blocking' ? XCircle : AlertTriangle;
  return <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${style.bg} ${style.border}`} data-id="element-8619">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.icon}`} data-id="element-8620" />
      <p className={`text-xs font-medium leading-relaxed ${style.text}`} data-id="element-8621">
        {warning.message}
      </p>
    </div>;
}
// ── Main component ──────────────────────────────────────────
export function AddLearnerSheet({
  open,
  onClose,
  activeAccount,
  classrooms,
  selectedClassroomId = null,
  onComplete
}: AddLearnerSheetProps) {
  const {
    accounts,
    createAndLinkStudent,
    linkTeacherToLearnerById,
    addLearnerToClassroom
  } = useAppStore();
  // ── Classroom context ──
  const resolvedClassroomId = selectedClassroomId || (classrooms.length === 1 ? classrooms[0].id : null);
  const [panelClassroomId, setPanelClassroomId] = useState<string>(resolvedClassroomId || '');
  const effectiveClassroomId = resolvedClassroomId || panelClassroomId || null;
  const activeClassroom = effectiveClassroomId ? classrooms.find(c => c.id === effectiveClassroomId) : null;
  // ── View state ──
  const [view, setView] = useState<SheetView>('input');
  const [query, setQuery] = useState('');
  const [selectedLearner, setSelectedLearner] = useState<LearnerAccount | null>(null);
  const [confirmAnyway, setConfirmAnyway] = useState(false);
  const [error, setError] = useState('');
  // New-student form
  const [newName, setNewName] = useState('');
  const [newGrade, setNewGrade] = useState<string>(activeClassroom ? String(activeClassroom.groupLevel) : '6');
  const [newClassroomId, setNewClassroomId] = useState('');
  // Success
  const [successMessage, setSuccessMessage] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  // Reset everything when the sheet opens fresh
  useEffect(() => {
    if (open) {
      setView('input');
      setQuery('');
      setSelectedLearner(null);
      setConfirmAnyway(false);
      setError('');
      setNewName('');
      setNewGrade(activeClassroom ? String(activeClassroom.groupLevel) : '6');
      setNewClassroomId('');
      setSuccessMessage('');
      setGeneratedCode('');
      setPanelClassroomId(resolvedClassroomId || '');
    }
    // Reset form ONLY when the sheet toggles open. Adding
    // activeClassroom/resolvedClassroomId to deps would wipe the user's
    // in-progress input every time those refs change while the sheet is
    // open (e.g. when accounts data refreshes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Sync grade default when classroom resolves later
  useEffect(() => {
    if (activeClassroom) {
      setNewGrade(String(activeClassroom.groupLevel));
    }
  }, [activeClassroom?.id, activeClassroom?.groupLevel]);
  // ── Pool of linkable learners (unlinked to this teacher) ──
  const linkedLearners = getLinkedLearners(accounts, activeAccount.id);
  const linkedIds = useMemo(() => new Set(linkedLearners.map(l => l.id)), [linkedLearners]);
  const availableLearners = useMemo(() => {
    const all: LearnerAccount[] = [];
    for (const acc of Object.values(accounts.accountsById)) {
      if (isLearnerAccount(acc) && !linkedIds.has(acc.id)) {
        all.push(acc);
      }
    }
    return all;
  }, [accounts.accountsById, linkedIds]);
  // ── Smart input interpretation ──
  const normalizedQuery = query.trim().toUpperCase();
  const looksLikeCode = CODE_PATTERN.test(normalizedQuery);
  // Code lookup (only when the query looks like a code)
  const codeMatch = useMemo<LearnerAccount | null>(() => {
    if (!looksLikeCode) return null;
    const found = findAccountByLinkCode(accounts, normalizedQuery);
    if (!found || !isLearnerAccount(found)) return null;
    return found;
  }, [accounts, normalizedQuery, looksLikeCode]);
  // Fuzzy search results
  const searchResults = useMemo<LearnerAccount[]>(() => {
    const trimmed = query.trim();
    if (looksLikeCode) return [];
    if (trimmed.length < SEARCH_MIN_LENGTH) return [];
    const q = trimmed.toLowerCase();
    const matches = availableLearners.filter(l => l.displayName.toLowerCase().includes(q) || l.username && l.username.toLowerCase().includes(q)).sort((a, b) => {
      // Prioritize grade-match if a classroom is active
      if (activeClassroom) {
        const aMatch = a.grade === activeClassroom.groupLevel ? 0 : 1;
        const bMatch = b.grade === activeClassroom.groupLevel ? 0 : 1;
        if (aMatch !== bMatch) return aMatch - bMatch;
      }
      return a.displayName.localeCompare(b.displayName);
    });
    return matches.slice(0, MAX_SEARCH_RESULTS);
  }, [query, looksLikeCode, availableLearners, activeClassroom]);
  const hasNoSearchResults = !looksLikeCode && query.trim().length >= SEARCH_MIN_LENGTH && searchResults.length === 0;
  const hasNoCodeMatch = looksLikeCode && !codeMatch;
  // ── Warnings for the selected learner ──
  const warnings = useMemo<LinkWarning[]>(() => {
    if (!selectedLearner) return [];
    if (!effectiveClassroomId) {
      return validateLink(accounts, {
        kind: 'teacher-direct',
        teacherId: activeAccount.id,
        learnerId: selectedLearner.id
      });
    }
    return validateLink(accounts, {
      kind: 'teacher-classroom',
      teacherId: activeAccount.id,
      classroomId: effectiveClassroomId,
      learnerId: selectedLearner.id
    });
  }, [selectedLearner, effectiveClassroomId, accounts, activeAccount.id]);
  const isBlocked = hasBlockingWarning(warnings);
  const needsConfirm = hasGatingWarning(warnings) && !isBlocked;
  // Flags for the selected learner's chip badges
  const selectedFlags = useMemo(() => selectedLearner ? getLearnerLinkageFlags(accounts, selectedLearner.id, {
    classroom: activeClassroom || null
  }) : null, [selectedLearner, accounts, activeClassroom]);
  // ── Handlers ──
  const handlePickLearner = (learner: LearnerAccount) => {
    setError('');
    setConfirmAnyway(false);
    setSelectedLearner(learner);
    setView('selected');
  };
  const handleConfirmLink = () => {
    if (!selectedLearner) return;
    if (isBlocked) return; // defensive
    if (needsConfirm && !confirmAnyway) {
      // Visual gating handled by button label; flip the flag
      setConfirmAnyway(true);
      return;
    }
    setError('');
    const learner = selectedLearner;
    try {
      // Link teacher↔learner if not yet linked
      if (!linkedIds.has(learner.id)) {
        linkTeacherToLearnerById(activeAccount.id, learner.id);
      }
      // Add to classroom if one is in context
      if (effectiveClassroomId && activeClassroom) {
        if (!activeClassroom.learnerIds.includes(learner.id)) {
          addLearnerToClassroom(activeAccount.id, effectiveClassroomId, learner.id);
        }
      }
      setSuccessMessage(`${learner.displayName} is succesvol ${effectiveClassroomId ? 'aan de klas' : 'aan je account'} toegevoegd!`);
      setGeneratedCode('');
      setView('success');
    } catch (err) {
      logError('Failed to link learner', err);
      setError('Er ging iets mis bij het koppelen. Probeer opnieuw.');
    }
  };
  const handleCreateNew = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const name = newName.trim();
    if (!name) {
      setError('Vul een naam in.');
      return;
    }
    const classroomId = effectiveClassroomId || newClassroomId || undefined;
    const grade = activeClassroom ? activeClassroom.groupLevel : parseInt(newGrade, 10) as 6 | 7 | 8;
    try {
      const {
        linkCode
      } = createAndLinkStudent(activeAccount.id, {
        displayName: name,
        grade
      }, classroomId);
      setGeneratedCode(linkCode);
      setSuccessMessage(`${name} is aangemaakt!`);
      setView('success');
    } catch (err) {
      logError('Failed to create learner', err);
      setError('Er ging iets mis bij het aanmaken.');
    }
  };
  const handleAddAnother = () => {
    setQuery('');
    setSelectedLearner(null);
    setConfirmAnyway(false);
    setError('');
    setNewName('');
    setNewClassroomId('');
    setSuccessMessage('');
    setGeneratedCode('');
    setView('input');
  };
  const handleClose = () => {
    onComplete?.();
    onClose();
  };
  // ── Header context line ──
  const contextLine = activeClassroom ? `${activeClassroom.name} · Groep ${activeClassroom.groupLevel} · ${activeClassroom.learnerIds.length} leerlingen` : classrooms.length === 0 ? 'Aan jouw account' : 'Kies eerst een klas';
  return <SlideOver open={open} onClose={handleClose} title="Leerling toevoegen" description={contextLine} width="lg" data-id="element-8622">
      <div className="space-y-5" data-id="element-8623">
        {/* Classroom picker if needed */}
        {!resolvedClassroomId && classrooms.length > 1 && view !== 'success' && <div className="flex items-start gap-3 p-3 bg-indigo-50/60 border border-indigo-100 rounded-xl" data-id="element-8624">
              <Info className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-8625" />
              <div className="flex-1 min-w-0" data-id="element-8626">
                <label className="text-xs font-bold text-indigo-700 block mb-1.5" data-id="element-8627">
                  Toevoegen aan klas
                </label>
                <select value={panelClassroomId} onChange={e => setPanelClassroomId(e.target.value)} className="w-full text-sm font-medium text-slate-800 bg-white border border-indigo-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-8628">
                  <option value="" data-id="element-8629">Geen klas (alleen koppelen)</option>
                  {classrooms.map(c => <option key={c.id} value={c.id} data-id="element-8630">
                      {c.name} (Groep {c.groupLevel})
                    </option>)}
                </select>
              </div>
            </div>}

        <AnimatePresence mode="wait" data-id="element-8631">
          {/* ── VIEW: smart input ─────────────────────────── */}
          {view === 'input' && <motion.div key="input" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} className="space-y-4" data-id="element-8632">
              <SmartInput value={query} onChange={setQuery} looksLikeCode={looksLikeCode} data-id="element-8633" />

              {/* Code match preview */}
              {looksLikeCode && codeMatch && <CodeMatchCard learner={codeMatch} onPick={() => handlePickLearner(codeMatch)} data-id="element-8634" />}

              {/* No code match */}
              {hasNoCodeMatch && <EmptyStateRow icon={<KeyRound className="w-4 h-4" data-id="element-8636" />} text={`Geen leerling gevonden met code "${normalizedQuery}".`} data-id="element-8635" />}

              {/* Search results */}
              {!looksLikeCode && searchResults.length > 0 && <div className="space-y-1.5" data-id="element-8637">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1" data-id="element-8638">
                    Bestaande leerlingen
                  </p>
                  <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100" data-id="element-8639">
                    {searchResults.map(learner => <LearnerResultRow key={learner.id} learner={learner} activeClassroom={activeClassroom || null} onPick={() => handlePickLearner(learner)} data-id="element-8640" />)}
                  </div>
                </div>}

              {/* No search results */}
              {hasNoSearchResults && <EmptyStateRow icon={<Search className="w-4 h-4" data-id="element-8642" />} text={`Geen leerling gevonden met "${query.trim()}".`} data-id="element-8641" />}

              {/* Always-on: create-new CTA */}
              <div className="pt-2" data-id="element-8643">
                <button onClick={() => {
              setNewName(!looksLikeCode && query.trim().length >= 2 ? query.trim() : '');
              setView('new');
            }} className="w-full flex items-center gap-3 p-3.5 rounded-xl border-2 border-dashed border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all group focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2" data-id="element-8644">
                  <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-8645">
                    <UserPlus className="w-4 h-4" data-id="element-8646" />
                  </div>
                  <div className="flex-1 text-left" data-id="element-8647">
                    <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-8648">
                      Maak nieuwe leerling aan
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-8649">
                      Genereer een koppelcode voor een nieuw account.
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-500 transition-colors" data-id="element-8650" />
                </button>
              </div>

              {/* Secondary: bulk import */}
              <div className="pt-2 border-t border-slate-100" data-id="element-8651">
                <button onClick={() => setView('bulk')} className="w-full flex items-center justify-center gap-2 py-2 text-xs font-bold text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-8652">
                  <Upload className="w-3.5 h-3.5" data-id="element-8653" />
                  Of importeer meerdere leerlingen tegelijk
                </button>
              </div>
            </motion.div>}

          {/* ── VIEW: selected (confirm + warnings) ───────── */}
          {view === 'selected' && selectedLearner && <motion.div key="selected" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} className="space-y-4" data-id="element-8654">
              <BackButton onClick={() => {
            setSelectedLearner(null);
            setConfirmAnyway(false);
            setView('input');
          }} data-id="element-8655" />

              {/* Big learner card */}
              <SelectedLearnerCard learner={selectedLearner} activeClassroom={activeClassroom || null} flags={selectedFlags} data-id="element-8656" />

              {/* Warnings */}
              {warnings.length > 0 && <div className="space-y-2" data-id="element-8657">
                  {warnings.map((w, i) => <WarningRow key={`${w.type}-${i}`} warning={w} data-id="element-8658" />)}
                </div>}

              {error && <p className="text-xs font-medium text-rose-600" data-id="element-8659">{error}</p>}

              {/* CTA */}
              <div className="flex flex-col gap-2 pt-2" data-id="element-8660">
                <Button onClick={handleConfirmLink} disabled={isBlocked} className={isBlocked ? 'w-full bg-slate-200 text-slate-400 cursor-not-allowed' : needsConfirm ? 'w-full bg-amber-500 hover:bg-amber-600 text-white' : 'w-full bg-indigo-600 hover:bg-indigo-700 text-white'} data-id="element-8661">
                  {isBlocked ? 'Niet beschikbaar' : needsConfirm ? `Toch toevoegen aan ${activeClassroom?.name || 'je account'}` : `Toevoegen aan ${activeClassroom?.name || 'je account'}`}
                </Button>
              </div>
            </motion.div>}

          {/* ── VIEW: new student form ────────────────────── */}
          {view === 'new' && <motion.form key="new" onSubmit={handleCreateNew} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} className="space-y-4" data-id="element-8662">
              <BackButton onClick={() => setView('input')} data-id="element-8663" />

              <div className="flex items-center gap-3 pb-2" data-id="element-8664">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center" data-id="element-8665">
                  <UserPlus className="w-5 h-5" data-id="element-8666" />
                </div>
                <div data-id="element-8667">
                  <h3 className="text-base font-bold text-slate-900" data-id="element-8668">
                    Nieuwe leerling
                  </h3>
                  <p className="text-xs text-slate-500" data-id="element-8669">
                    We genereren een koppelcode voor het inloggen.
                  </p>
                </div>
              </div>

              <Input variant="admin" label="Naam van de leerling" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Bijv. Sanne Jansen" autoFocus data-id="element-8670" />

              {!activeClassroom ? <>
                  <Select variant="admin" label="Groep" value={newGrade} onChange={e => setNewGrade(e.target.value)} options={[{
              value: '6',
              label: 'Groep 6'
            }, {
              value: '7',
              label: 'Groep 7'
            }, {
              value: '8',
              label: 'Groep 8'
            }]} data-id="element-8671" />
                  {classrooms.length > 0 && <Select variant="admin" label="Klas (optioneel)" value={newClassroomId} onChange={e => setNewClassroomId(e.target.value)} options={[{
              value: '',
              label: 'Geen klas — alleen koppelen'
            }, ...classrooms.map(c => ({
              value: c.id,
              label: `${c.name} (Groep ${c.groupLevel})`
            }))]} data-id="element-8672" />}
                </> : <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-100 rounded-xl" data-id="element-8673">
                  <GraduationCap className="w-4 h-4 text-slate-400" data-id="element-8674" />
                  <p className="text-xs text-slate-600" data-id="element-8675">
                    Wordt toegevoegd aan{' '}
                    <strong className="text-slate-900" data-id="element-8676">
                      {activeClassroom.name}
                    </strong>{' '}
                    (Groep {activeClassroom.groupLevel}).
                  </p>
                </div>}

              {error && <p className="text-xs font-medium text-rose-600" data-id="element-8677">{error}</p>}

              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-id="element-8678">
                Leerling aanmaken
              </Button>
            </motion.form>}

          {/* ── VIEW: bulk import ─────────────────────────── */}
          {view === 'bulk' && <motion.div key="bulk" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} className="space-y-4" data-id="element-8679">
              <BackButton onClick={() => setView('input')} data-id="element-8680" />
              {activeClassroom && <div className="flex items-start gap-2.5 p-3 bg-emerald-50 border border-emerald-100 rounded-xl" data-id="element-8681">
                  <Info className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-8682" />
                  <p className="text-xs text-emerald-800" data-id="element-8683">
                    Alle leerlingen worden toegevoegd aan{' '}
                    <strong data-id="element-8684">{activeClassroom.name}</strong> (Groep{' '}
                    {activeClassroom.groupLevel}).
                  </p>
                </div>}
              <BulkImport teacherId={activeAccount.id} onComplete={handleClose} onCancel={() => setView('input')} classrooms={classrooms} selectedClassroomId={activeClassroom ? activeClassroom.id : selectedClassroomId} data-id="element-8685" />
            </motion.div>}

          {/* ── VIEW: success ─────────────────────────────── */}
          {view === 'success' && <motion.div key="success" initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18
        }} data-id="element-8686">
              <SuccessStep formSuccess={successMessage} generatedCode={generatedCode} onAddAnother={handleAddAnother} onComplete={handleClose} data-id="element-8687" />
            </motion.div>}
        </AnimatePresence>
      </div>
    </SlideOver>;
}
// ── Sub-components ───────────────────────────────────────────
function SmartInput({
  value,
  onChange,
  looksLikeCode
}: {
  value: string;
  onChange: (v: string) => void;
  looksLikeCode: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  return <div className="relative" data-id="element-8688">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" data-id="element-8689">
        {looksLikeCode ? <KeyRound className="w-4 h-4" data-id="element-8690" /> : <Search className="w-4 h-4" data-id="element-8691" />}
      </div>
      <input ref={inputRef} type="text" value={value} onChange={e => onChange(e.target.value)} placeholder="Zoek op naam of plak een koppelcode" className="w-full pl-10 pr-10 py-3 text-sm font-medium bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder:text-slate-400" autoComplete="off" spellCheck={false} data-id="element-8692" />
      {value && <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100" aria-label="Wissen" data-id="element-8693">
          <X className="w-3.5 h-3.5" data-id="element-8694" />
        </button>}
    </div>;
}
function CodeMatchCard({
  learner,
  onPick
}: {
  learner: LearnerAccount;
  onPick: () => void;
}) {
  return <button onClick={onPick} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-emerald-50 border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100/60 transition-all text-left focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2" data-id="element-8695">
      <div className="w-9 h-9 rounded-lg bg-emerald-200 text-emerald-700 flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-8696">
        {learner.displayName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0" data-id="element-8697">
        <div className="flex items-center gap-2" data-id="element-8698">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" data-id="element-8699" />
          <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider" data-id="element-8700">
            Code gevonden
          </span>
        </div>
        <div className="text-sm font-bold text-slate-900 mt-0.5 truncate" data-id="element-8701">
          {learner.displayName}
        </div>
        {learner.grade && <div className="text-xs text-slate-500 mt-0.5" data-id="element-8702">
            Groep {learner.grade}
          </div>}
      </div>
      <ChevronRight className="w-4 h-4 text-emerald-600 flex-shrink-0" data-id="element-8703" />
    </button>;
}
function LearnerResultRow({
  learner,
  activeClassroom,
  onPick
}: {
  learner: LearnerAccount;
  activeClassroom: Classroom | null;
  onPick: () => void;
}) {
  const gradeMismatch = activeClassroom && learner.grade && learner.grade !== activeClassroom.groupLevel;
  return <button onClick={onPick} className="w-full flex items-center gap-3 p-3 hover:bg-indigo-50/40 transition-colors text-left focus:outline-none focus:bg-indigo-50/60" data-id="element-8704">
      <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-xs flex-shrink-0" data-id="element-8705">
        {learner.displayName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0" data-id="element-8706">
        <div className="text-sm font-bold text-slate-900 truncate" data-id="element-8707">
          {learner.displayName}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5" data-id="element-8708">
          {learner.grade ? <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${gradeMismatch ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-8709">
              Gr {learner.grade}
            </span> : <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" data-id="element-8710">
              Geen groep
            </span>}
          {learner.username && <span className="text-[11px] text-slate-400 truncate" data-id="element-8711">
              @{learner.username}
            </span>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" data-id="element-8712" />
    </button>;
}
function SelectedLearnerCard({
  learner,
  activeClassroom,
  flags
}: {
  learner: LearnerAccount;
  activeClassroom: Classroom | null;
  flags: ReturnType<typeof getLearnerLinkageFlags> | null;
}) {
  return <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200" data-id="element-8713">
      <div className="flex items-center gap-3" data-id="element-8714">
        <div className="w-12 h-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0" data-id="element-8715">
          {learner.displayName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0" data-id="element-8716">
          <div className="text-base font-black text-slate-900 truncate" data-id="element-8717">
            {learner.displayName}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap" data-id="element-8718">
            {learner.grade ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded bg-white text-slate-600 border border-slate-200" data-id="element-8719">
                <GraduationCap className="w-3 h-3" data-id="element-8720" />
                Groep {learner.grade}
              </span> : <span className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded bg-white text-slate-500 border border-slate-200" data-id="element-8721">
                <GraduationCap className="w-3 h-3" data-id="element-8722" />
                Geen groep
              </span>}
            {flags && (flags.multiClassroom || flags.multiTeacher) && <span className="inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700" title={flags.otherEnrollments.length > 0 ? `Ook in: ${flags.otherEnrollments.map(e => e.classroomName).join(', ')}` : 'Meerdere koppelingen'} data-id="element-8723">
                <Link2 className="w-3 h-3" data-id="element-8724" />
                {flags.otherEnrollments.length > 0 ? `${flags.otherEnrollments.length} andere ${flags.otherEnrollments.length === 1 ? 'klas' : 'klassen'}` : 'Meerdere koppelingen'}
              </span>}
          </div>
        </div>
      </div>
      {activeClassroom && <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500" data-id="element-8725">
          Toevoegen aan{' '}
          <strong className="text-slate-700" data-id="element-8726">{activeClassroom.name}</strong>{' '}
          (Groep {activeClassroom.groupLevel})
        </div>}
    </div>;
}
function EmptyStateRow({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100 text-slate-500" data-id="element-8727">
      <span className="text-slate-400" data-id="element-8728">{icon}</span>
      <span className="text-xs font-medium" data-id="element-8729">{text}</span>
    </div>;
}
function BackButton({
  onClick
}: {
  onClick: () => void;
}) {
  return <button onClick={onClick} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-indigo-600 transition-colors" data-id="element-8730">
      <ArrowLeft className="w-3.5 h-3.5" data-id="element-8731" />
      Terug
    </button>;
}